import pick from 'lodash/pick';
import { rHandler } from '@/plugins/RHandler';
import { apiService } from '@/services/APIService';
import { DEFAULT_PARAMS } from '@/utils/objectsRHandler';
import { assertActive, signChallenge, signingErrorMessage, SignatureClientError } from '@/modules/UIDesign/Auth/services/cryptoPro';
// TEMP_SIGNATURE_PREVIEW: remove this import and the guarded signature getter branch.
import { isSignaturePreview, signaturePreview } from '@/modules/UIDesign/Auth/services/signaturePreview';

export const AuthManager = {
  confirmEmail: ({ data } = DEFAULT_PARAMS) => rHandler(
    apiService.post(`confirm-email`, data),
    (response) => ({ response }),
    (error) => ({ error }),
  ),
  confirmEmailVerify: ({ data } = DEFAULT_PARAMS) => rHandler(
    apiService.post(`confirm-email/confirm`, data),
    (response) => ({ response }),
    (error) => ({ error }),
  ),
  get signature() {
    // TEMP_SIGNATURE_PREVIEW
    if (isSignaturePreview) return signaturePreview.signature;
    const url = 'auth/signature';
    return {
      challenge: ({ params } = DEFAULT_PARAMS) => rHandler(
        apiService.get(`${url}/challenge`, params),
        (response) => ({
          response,
          item: response?.data?.data, // { challenge, challenge_id, expires_at }
        }),
        (error) => ({ error }),
      ),
      verify: ({ data } = DEFAULT_PARAMS) => rHandler( // { challenge_id, signature: 'отделенная CAdES-BES подпись в Base64' }
        apiService.post(`${url}/verify`, data),
        (response) => ({
          response,
          item: response?.data?.data, // { verification_token, organizations }
        }),
        (error) => ({ error }),
      ),
      login: ({ data, obs } = DEFAULT_PARAMS) => rHandler( // { verification_token, organization_id, password?, password_confirmation? }
        apiService.post(`${url}/login`, data),
        (response) => ({ response }),
        (error) => ({ error }),
        obs,
      ),
      async authorize({ thumbprint, isActive = () => true }) {
        assertActive(isActive);
        const challengeResult = await AuthManager.signature.challenge();
        if (challengeResult.error) return challengeResult;
        assertActive(isActive);
        const { challenge, challenge_id, expires_at } = challengeResult.item || {};
        const expiresAt = Date.parse(expires_at);
        if (!challenge || !challenge_id || !Number.isFinite(expiresAt)) {
          throw new SignatureClientError('Сервер вернул некорректный запрос на подписание. Повторите попытку.');
        }
        if (expiresAt <= Date.now()) throw new SignatureClientError('Время подписания истекло. Повторите попытку.');
        let signature;
        try {
          signature = await signChallenge(challenge, thumbprint, isActive);
        } catch (error) {
          throw new SignatureClientError(signingErrorMessage(error));
        }
        assertActive(isActive);
        if (expiresAt <= Date.now()) throw new SignatureClientError('Время подписания истекло. Повторите попытку.');
        return AuthManager.signature.verify({ data: { challenge_id, signature } });
      },
      async setupPassword({ data, thumbprint, organizationId, isActive, obs }) {
        const result = await AuthManager.signature.authorize({ thumbprint, isActive });
        if (result.error) return result;
        assertActive(isActive);
        const { verification_token, organizations = [] } = result.item || {};
        if (!verification_token || !organizations.some(({ id }) => id === organizationId)) {
          throw new SignatureClientError('Выбранная подпись не разрешает настройку этой организации.');
        }
        return AuthManager.signature.login({
          data: { verification_token, organization_id: organizationId, ...pick(data, ['password', 'password_confirmation']) },
          obs,
        });
      },
    };
  },
  get accountSetup() {
    // TEMP_SIGNATURE_PREVIEW
    if (isSignaturePreview) return signaturePreview.accountSetup;
    return {
      async removeEmailChange({ args: [id], isActive = () => true } = DEFAULT_PARAMS) {
        assertActive(isActive);
        return rHandler(
          apiService.delete(`user/change-email/${id}`),
          (response) => ({ response }),
          (error) => ({ error }),
        );
      },
      sendEmailChange: ({ data, obs } = DEFAULT_PARAMS) => rHandler(
        apiService.put('user/email', pick(data, ['email'])),
        (response) => ({ response }),
        (error) => ({ error }),
        obs,
      ),
      confirmEmailChange: ({ data, obs } = DEFAULT_PARAMS) => rHandler(
        apiService.put('user/email-confirm', pick(data, ['email', 'code'])),
        (response) => ({ response }),
        (error) => ({ error }),
        obs,
      ),
      setOrganizationEmail: ({ args: [id], data, obs } = DEFAULT_PARAMS) => rHandler(
        apiService.put(`organizers/${id}`, { contact_email: data.email }),
        (response) => ({ response }),
        (error) => ({ error }),
        obs,
      ),
    };
  },
};
